import {
  find,
  has,
  identity,
  includes,
  isDate,
  isEmpty,
  isNil,
  isNull,
  map,
  mapKeys,
  startCase,
  sum,
} from 'lodash';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import numeral from 'numeral';
import pluralize from 'pluralize';
import {
  Fund,
  isPercent,
  isShares,
  Ranking,
} from 'src/components/Exchange/types';
import { ItemSelection } from 'src/types';
import words from 'src/utils/words';
import {
  CORE_CUSTOMER,
  HIGH_NET_WORTH,
  MILLION_CUSTOMER,
  NEW_1_CUSTOMER,
  NEW_2_CUSTOMER,
  PREFERED_CUSTOMER,
  US_STATES,
  US_STATE_CODE,
  VIP_CUSTOMER,
  withHoldStateRegardless,
  pershingVoluntaryStateWithholdings,
} from '../settings/settings';
import { Predicate } from 'src/types';
import { UNIT } from 'src/settings/settings';
let rePhraseRulesLower;

/**
 * Applies TRP rephrase rules for common phrases to make them consistent, like
 * "401k" -> "401(k)" and "FAQ" -> "Frequently Asked Questions"
 *
 * @param source
 * @return {*}
 */
export function applyTrpRephraseRules(source) {
  if (typeof source !== 'string') {
    return '';
  }

  const result = source.toLowerCase();

  if (rePhraseRulesLower === undefined) {
    if (window.trpsearch === undefined) {
      return result;
    }

    const rePhraseRules = window.trpsearch.rePhraseRules;
    rePhraseRulesLower = mapKeys(rePhraseRules, (_value, key) =>
      key.toLowerCase()
    );
  }

  return rePhraseRulesLower[result] || result;
}

/**
 * This function perform checking of addBankSelection in redux form
 * @param {Object} values: redux form values
 * @param {Object} bank: bank object
 */
export function checkBankEquivalence(values, bank) {
  switch (typeof values.addBankSelection) {
    case 'string':
      return bank.accountNumber === values.addBankSelection;

    case 'object':
      return (
        values.addBankSelection !== null &&
        bank.accountNumber === values.addBankSelection.accountNumber
      );

    default:
      return false;
  }
}

/**
 * Returns the first value that is not undefined, null, or NaN. If every value is one of these, then
 * the last value is returned.
 *
 * @param {*} values Values to test and first non-undefined/null/NaN value is returned
 */
export function coalesce(...values) {
  const len = values.length;
  if (len === 0) {
    return null;
  }
  for (let i = 0; i < len; i++) {
    const value = values[i];
    if (
      value !== undefined &&
      value !== null &&
      !(typeof value === 'number' && isNaN(value))
    ) {
      return value;
    }
  }
  return values[len - 1];
}

/**
 * Counts how many items in an array match a particular condition.
 * @param {Array} array
 * @param {Function} predicate
 * @returns {number} How many items predicate returns true for, or
 * zero if array is not an array or predicate is not a function
 */
export function countArray(array, predicate) {
  return !Array.isArray(array) || typeof predicate !== 'function'
    ? 0
    : array.reduce((count, item) => count + (predicate(item) ? 1 : 0), 0);
}

/**
 * create name object that to pass to addBank in eVerification module
 * @param firstName
 * @param lastName
 * @param lastName
 */
export function createNameObject(firstName, lastName) {
  return firstName && lastName ? { firstName, lastName } : {};
}

export function currency(value) {
  return numeral(value).format('$0,0.00');
}

export function currencyNoPrecision(value) {
  return numeral(value).format('$0,0');
}

/**
 * Groups customer segment name into bucket types,
 * since most feature functionality is grouped by either
 * Core, or High value customers.
 *
 * @param {string} customerSegmentName
 * @returns {string}
 */
export function customerSegmentBucketType(customerSegmentName) {
  switch (customerSegmentName) {
    case CORE_CUSTOMER:
    case NEW_1_CUSTOMER:
    case NEW_2_CUSTOMER:
    case PREFERED_CUSTOMER:
    case VIP_CUSTOMER:
      return CORE_CUSTOMER;

    case HIGH_NET_WORTH:
    case MILLION_CUSTOMER:
      return HIGH_NET_WORTH;

    default:
      return CORE_CUSTOMER;
  }
}

/**
 * Returns true if two values are strictly equal, both either null or undefined, or are equal value dates.
 *
 * @param {any} x First value to test
 * @param {any} y Second value to test
 * @return {boolean} True if values are equivalent as defined above.
 */
export function equiv(x, y) {
  if (x === y) {
    return true;
  }
  if (x === undefined || y === undefined) {
    return x === null || y === null;
  } else if (x === null || y === null) {
    return false;
  }
  // for date objects, but can apply to others and would be valid
  if (x.valueOf && y.valueOf) {
    return x.valueOf() === y.valueOf();
  }
  return false;
}

export type ShareValuesAndUnits = Pick<Fund, 'grossUnit' | 'grossAmount'>;

export const conditionallyFormatValue = ({
  grossAmount,
  grossUnit,
}: ShareValuesAndUnits) => {
  if (isShares(grossUnit)) {
    return formatShares(grossAmount, words.share);
  }

  if (isPercent(grossUnit)) {
    return formatPercent(grossAmount);
  }
  return currency(grossAmount);
};
/**
 * Reduce function takes an array of values from an array of objects composed of
 * label and value key pairs.
 */
export const extractValueFromLabelValuePairArray = (
  keyValPairArray: ItemSelection[]
) =>
  keyValPairArray
    ?.filter(
      (ObjLabelValue) =>
        !isNil(ObjLabelValue) &&
        !isNil(ObjLabelValue?.value) &&
        !isNil(ObjLabelValue?.label)
    )
    ?.map(({ value }) => value);

/**
 * Formats a percentage number with two decimal places and percent sign.
 */
export function formatPercent(percent: number) {
  return numeral(percent).format(percent ? '0.00' : '0') + '%';
}

/**
 * Formats a number of shares with three decimal places.
 * Variadic Function that can take 1, 2, or 3 arguments
 * One parameter: format a number
 * Two params: Format number [space] PluralNoun
 * Three params Format number [space] adjective [space] PluralNoun
 */
// overloaded signatures
export function formatShares(shareCount: number): string;
export function formatShares(shareCount: number, noun: string): string;
export function formatShares(
  shareCount: number,
  adjective: string,
  noun: string
): string;
// implementation
export function formatShares(
  shareCount: number,
  adjective?: string,
  noun?: string
) {
  let sentence: string[] = [];
  const formattedValue = numeral(shareCount).format(
    shareCount ? '0,0.000' : '0'
  );

  // one parameter
  if (shareCount && !adjective && !noun) {
    return formattedValue;
  }
  const pluralNoun = pluralize(noun || adjective || '', shareCount);

  // two parameters
  if (!noun && adjective) {
    sentence = [formattedValue, pluralNoun];
  } else {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    sentence = [formattedValue, adjective!, pluralNoun];
  }

  return sentence
    .filter((x) => x)
    .join(' ')
    .trim();
}

/**
 * Returns the full state name for a state code, or the state code if not listed.
 */
export const fullStateName = (stateCode: US_STATE_CODE): string =>
  coalesce(US_STATES[stateCode], stateCode, '');

/**
 * @param {Object} entitySegment comes from personalInformation store object
 */
export const getClientType = (entitySegment) => {
  let { enumerationName = '' } = entitySegment || {};
  enumerationName = enumerationName.toLowerCase();

  if (enumerationName.includes('preferred')) {
    return 'preferred';
  }
  if (enumerationName.includes('high')) {
    return 'high_value';
  }
  if (enumerationName.includes('enhanced')) {
    return 'enhanced_personal';
  }
  if (enumerationName.includes('personal')) {
    return 'personal';
  }
  return 'core';
};

export function getFormattedBankName(bank) {
  return has(bank, 'bankName') &&
    has(bank, 'accountType') &&
    has(bank, 'accountNumber')
    ? `${bank.bankName} - ${bank.accountType} ${bank.accountNumber}`
    : null;
}

/**
 * Returns formatted phone number
 * i.e. 1234567890 to 123-456-7890
 *
 * @param phoneNumber
 * @return {string}
 */
export function formatUSPhoneNumber(phoneNumber) {
  if (typeof phoneNumber === 'undefined') {
    throw new Error('Phone number is undefined.');
  }
  // Remove any existing non-digit characters from the phone number
  phoneNumber = phoneNumber.replace(/\D/g, '');
  // Check if the phone number is of valid length
  if (phoneNumber.length !== 10) {
    throw new Error('Invalid phone number length. Expected 10 digits.');
  }
  // Format the phone number with hyphens
  const formattedNumber = `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6)}`;
  return formattedNumber;
}

/**
 * Returns formatted string to have first letter capitalized
 * i.e. 1234567890 to 123-456-7890
 *
 * @param str
 * @return {string}
 */
export function capitalizeFirstLetter(str) {
  if (typeof str === 'undefined') {
    return ''; // Return an empty string if the input is undefined
  }
  return str.charAt(0).toUpperCase() + str.substring(1).toLowerCase();
}

/**
 * Returns fund units
 *
 * @param fundUnit
 * @param exchangeUnit
 * @return {UNIT}
 */
export function effectiveDisplayUnit(fundUnit, exchangeUnit) {
  return fundUnit === UNIT.DOLLARS && exchangeUnit !== UNIT.DOLLARS
    ? UNIT.SHARES
    : fundUnit;
}

/**
 * Returns true if the input truely has a value--non-empty string, non-NaN number (zero returns true, has value),
 * any function reference, a Date object with a valid date, array with at least one element, object with
 * at least one key.
 *
 * @param value
 * @return {boolean}
 */
export function hasValue(value): boolean {
  switch (typeof value) {
    case 'string':
      return value !== '';

    case 'number':
      return !isNaN(value);

    case 'boolean':
    case 'function':
      return true;

    default:
      return isDate(value) ? !isNaN(value.getTime()) : !isEmpty(value);
  }
}

export function ignorePromiseError<T>(promise: Promise<T>) {
  return new Promise((resolve) =>
    promise.then(resolve).catch(() => resolve(undefined))
  );
}

export function isEnhancedPersonal(clientType) {
  return clientType === 'enhanced_personal';
}

export function isHighValue(clientType) {
  return clientType === 'high_value';
}

export function isJustAdded(bank) {
  if (!bank) {
    return false;
  }

  if (bank.sameDayBank) {
    return true;
  }
  if (!bank.externallyVerifiedBank || isNull(bank.bankVerificationDate)) {
    return false;
  }

  const today = new Date();
  const bankVerificationDate = moment(bank.bankVerificationDate).toDate();
  const bankYear = bankVerificationDate.getFullYear();
  const bankMonth = bankVerificationDate.getMonth();
  const bankDay = bankVerificationDate.getDate();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();

  return (
    bankYear === todayYear && bankMonth === todayMonth && bankDay === todayDate
  );
}

export function isMocks() {
  const queryString = window.location.search;
  return queryString ? queryString.replace('?', '').includes('mocks') : false;
}

export function isPositiveNumber(value) {
  return typeof value === 'number' && !isNaN(value) && value > 0;
}

export function isPreferred(clientType) {
  return clientType === 'preferred';
}

const SCALAR_TYPES = {
  string: true,
  number: true,
  boolean: true,
};
export function isScalar(value) {
  return !!SCALAR_TYPES[typeof value];
}

export function isTrueLike(value) {
  switch (typeof value) {
    case 'string':
      return value !== '';

    case 'number':
      return !isNaN(value) && value;

    case 'boolean':
      return value;

    default:
      return isDate(value) ? !isNaN(value.getTime()) : value;
  }
}

export function isUsingMocks() {
  /* eslint-disable no-restricted-globals */
  return (
    location &&
    location.hostname === 'localhost' &&
    /mocks?/.test(location.search)
  );
  /* eslint-enable no-restricted-globals */
}

export function mapFundAllocation(fund: Fund) {
  const stockMap = [
    'Domestic Stock',
    'Domestic Stocks',
    'Foreign Stock',
    'Foreign Stocks',
    'Preferred Stock',
    'Preferred Stocks',
  ];
  const bondMap = [
    'Foreign Bonds',
    'Foreign Bond',
    'Domestic Bond',
    'Domestic Bonds',
  ];
  const shortTermMap = [
    'Convertibles',
    'Convertible',
    'Cash',
    'Options',
    'Option',
    'Other',
  ];
  const bonds: Ranking['percentTotalNetAssets'][] = [];
  const stocks: Ranking['percentTotalNetAssets'][] = [];
  const shortTermReserves: Ranking['percentTotalNetAssets'][] = [];
  let asOfDate = undefined;
  const rankings = fund.assetAllocationRankings;
  // this is meant to roll up those funds in the maps above to new allocation data points stocks, bonds, STR
  map(rankings, (ranking) => {
    if (includes(stockMap, ranking.name)) {
      stocks.push(ranking.percentTotalNetAssets);
    } else {
      stocks.push(0);
    }
    if (includes(bondMap, ranking.name)) {
      bonds.push(ranking.percentTotalNetAssets);
    } else {
      bonds.push(0);
    }
    if (includes(shortTermMap, ranking.name)) {
      shortTermReserves.push(ranking.percentTotalNetAssets);
    } else {
      shortTermReserves.push(0);
    }
    asOfDate = asOfDate || ranking.asOfDate;
  });
  // add percentages to get full picture at fund level of percent of new categories
  fund.allocation = {
    stocks: Math.round(sum(stocks)),
    bonds: Math.round(sum(bonds)),
    shortTermReserves: Math.round(sum(shortTermReserves)),
    asOfDate,
  };
  return fund;
}

export const roundDownToNearestDecimalPlace = (num, place) => {
  const stringNum = `${num}`;
  const indexOfDecimalPoint = stringNum.indexOf('.');
  return parseFloat(
    stringNum.slice(
      0,
      indexOfDecimalPoint > -1
        ? indexOfDecimalPoint + 1 + place
        : stringNum.length
    )
  );
  // return Math.floor(num * 100) / 100;
};

export const roundDownToNearestHundredthsPlace = (num) =>
  roundDownToNearestDecimalPlace(num, 2);

/**
 * Returns the current year in the New York timezone, used in some specific date checks.
 */
export function newYorkYear() {
  return momentTimezone().tz('America/NEW_YORK').year();
}

/**
 * Returns a function that when called passes args on to the composed function and negates the result.
 * @param {function} fn
 * @returns {function}
 */

export function not(fn: CallableFunction): Predicate {
  return function notImpl(...args) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /** @ts-ignore */
    return !fn.apply(this, args);
  };
}

/**
 * Given a value, returns empty string if it is undefined or null, and returns the value otherwise.
 *
 *  @param {*} value Value to test and return if not undefined or null
 * @return {*} value if not undefined or null, empty string otherwise
 */
export function orEmpty(value) {
  return value === undefined || value === null ? '' : value;
}

/**
 * Sorter that picks a property that is a string and sorts by those strings.
 * @param {function} getter
 * @returns {function}
 */
export function stringPropSorter(getter) {
  return function stringPropSorterImpl(x, y) {
    if (x === y) {
      return 0;
    }
    const xn = isNil(x);
    const yn = isNil(y);
    if (xn && yn) {
      return 0;
    }
    if (xn) {
      return 1;
    }
    if (yn) {
      return -1;
    }
    const xVal = getter(x);
    const yVal = getter(y);
    const xStr = typeof xVal === 'string';
    const yStr = typeof yVal === 'string';
    if (xStr && yStr) {
      return xVal.localeCompare(yVal);
    }
    if (xStr) {
      return -1;
    }
    if (yStr) {
      return 1;
    }
    return 0;
  };
}

export function sortFundAllocations(funds: Fund[], performance, composition) {
  // bb just stands for a new array of type b with sorted values, order is ascending alphabetical (s
  // order all funds by ticker to insure we a merging the correct funds
  const bigOne: Fund[] = [];
  funds.forEach((fund) => {
    const fundComp = find(
      composition,
      (compositionFund) =>
        compositionFund.investmentVehicleKey.ticker ===
        fund.investmentVehicleKey.ticker
    ) as Fund;

    const fundPerformance = find(
      performance,
      (performanceFund) =>
        performanceFund.investmentVehicleKey.ticker ===
        fund.investmentVehicleKey.ticker
    ) as Fund;

    const combined = Object.assign({}, fundComp, fundPerformance, fund);
    if (combined.investmentVehicleKey.trpcode === 'ICM') {
      return;
    }

    bigOne.push(combined);
  });
  return bigOne;
}

/**
 * Sums up the values of an array, optionally with a getter to get a property out of an array of objects,
 * and optionally with a default value if the input is not an array.
 *
 * @param array
 * @param getter
 * @param defaultValue
 * @returns {*}
 */
export function sumArray(array, getter = (i) => i, defaultValue?: number) {
  if (!Array.isArray(array)) {
    return defaultValue;
  }
  return (array as number[]).reduce((acc, val) => acc + getter(val), 0);
}

/**
 *
 * Quick sort of number or date params
 *
 */

export function numericSorter(d1: number, d2: number) {
  return d1 > d2 ? 1 : -1;
}

/**
 * Creates a new function that wraps a function and returns a cached result when called with the
 * exact same arguments as the previous call. Differences from _.memoize are it takes all arguments
 * into account, not just the first, it does a strict equality check on all args, and instead of holding
 * a complex cache it only holds the most recent result. Will not cache null or undefined result.
 *
 * @param func
 * @returns {function}
 */
export function strictMemoize(func: CallableFunction) {
  let lastArgs = [];
  let lastResult = null;
  return function strictMemoizeImpl(...args) {
    if (
      !isNil(lastResult) &&
      args.length === lastArgs.length &&
      args.every((arg, i) => arg === lastArgs[i])
    ) {
      return lastResult;
    }

    /* @ts-expect-error CallableFunction and 'this' scope problems; consider refactoring as arrows */
    const nextResult = func.apply(this, args);
    if (isNil(nextResult)) {
      lastResult = null;
    } else {
      /* @ts-expect-error CallableFunction args problems; consider refactoring as arrows */
      lastArgs = args;

      /* @ts-expect-error CallableFunction function return problems; consider refactoring as arrows */
      lastResult = nextResult;
    }
    return nextResult;
  };
}

/**
 * Given an array, returns a lookup map for each item in the array, where the key is the key provided
 * by the key picker callback or the item itself if no callback is provided.
 *
 * @param array
 * @param keyPicker
 * @param itemConverter
 * @return {{}}
 */
export function toLookup<T, S = T>(
  array: T[],
  keyPicker: (item: T) => string = identity,
  itemConverter: (item: T) => S = identity
) {
  const lookup: Record<string, S> = {};
  if (Array.isArray(array)) {
    array.forEach((item) => (lookup[keyPicker(item)] = itemConverter(item)));
  }
  return lookup;
}

/**
 * This function replaces the starting letter of each word UpperCase (eg: NON RETIREMENT to Non Retirement),
 * does not affect symbols
 *
 * @param {string} str
 * @return {string} str
 */
export function titleCase(str: string): string {
  return str ? startCase(String(str).toLowerCase()) : '';
}

/**
 * This function replaces the underscore in the string with hyphen and makes the starting letter of each
 * word UpperCase (eg: NON_RETIREMENT to Non-Retirement)
 *
 * @param {string} str
 * @return {string} str
 */
export function trainCase(str: string): string {
  return str
    ? str
        .replace('_', ' ')
        .toLowerCase()
        .replace(/(^| )(\w)/g, (match) => match.toUpperCase().replace(' ', '-'))
    : str;
}

export function truncateToTwoDecimalFormatted(value) {
  // while user is typing we don't want to round because if they type two decimals and then a third we don't want
  // to change the second decimal value actually typed, which then proceeds to cause worse issues if they
  // keep typing, so format with extra decimals and truncate
  const formatted = numeral(value).format('$0,0[.]000000');
  const decIndex = formatted.indexOf('.');
  return decIndex === -1 ? formatted : formatted.substr(0, decIndex + 3);
}

/**
 * function to map boolean values to 'Y/N' because analytics can't support
 *  @param {Boolean} bool
 *  @return {string} true/"Y" or false/"N"
 */
export function boolToYesNo(bool) {
  if (isNil(bool)) {
    return 'N';
  }
  return bool ? 'Y' : 'N';
}

/**
 * Apply proper grammar to a list so it includes ','s and 'and' in accordance with the Oxford Comma.
 * If any of the entries in the array are objects this returns an array otherwise it returns a `join`-ed string
 *
 * @param {Array} arr
 * @returns {Array|string}
 */
export const arrayToSentence = (arr: string[] = []) => {
  const shouldConvertToString = !arr.some(
    (entry) => (entry as unknown) instanceof Object
  );
  if (arr.length < 2) {
    return shouldConvertToString ? arr[0] : arr;
  }

  const last = arr.pop();
  const first = arr.shift();

  if (arr.length === 0) {
    return shouldConvertToString
      ? `${first} and ${last}`
      : [first, ' and ', last];
  }

  const res = [first];
  for (const entry of arr) {
    res.push(', ');
    res.push(entry);
  }
  res.push(', and ');
  res.push(last);
  return shouldConvertToString ? res.join('') : res;
};

export const toLowerKebabCase = (str) => {
  return str.toLowerCase().replace(/\s+/g, '-');
};

export const getQueryStringValue = (key) => {
  return decodeURIComponent(
    window.location.search.replace(
      new RegExp(
        '^(?:.*[&\\?]' +
          encodeURIComponent(key).replace(/\s+/g, '\\$&') +
          '(?:\\=([^&]*))?)?' +
          '.*$',
        'i'
      ),
      '$1'
    )
  );
};

export const getStudentName = (name: string) => {
  const fullName = name?.split(' ');
  let firstName = '',
    middleName = '',
    lastName = '',
    suffix = '';
  if (fullName?.length === 1) {
    firstName = fullName[0];
  } else if (fullName?.length === 2) {
    [firstName, lastName] = fullName;
  } else if (fullName?.length === 3) {
    [firstName, middleName, lastName] = fullName;
  } else if (fullName?.length === 4) {
    [firstName, middleName, lastName, suffix] = fullName;
  }
  return {
    firstName,
    middleName,
    lastName,
    suffix,
  };
};
//Might need to copy this into ExpandedAutomaticTransactions for state tax
export const compareEventDateToCurrentDate = (futureDate) => {
  const eventDate = new Date(futureDate).getTime();
  const currentDate = new Date().getTime();

  //if the event date hasn't happened yet return false
  if (eventDate > currentDate) {
    return false;
  }
  return true;
};

export const charitiesThatMatchDeliveryType = (charities, deliveryMethod) => {
  return charities.filter(
    (charity) => charity.deliveryMethod === deliveryMethod
  );
};

export const isFundExchangeToNew = (fund) => {
  return fund?.deliveryType?.enumerationName === 'ExchangeToNew';
};

export const exchangeToNewFund = (rmd, allFunds) => {
  return allFunds.filter(
    (fund) => fund.rkpCode === rmd[0].targetAgreementKey.agreementID
  );
};

export const isStateWithholdRegardless = (state) => {
  return withHoldStateRegardless.indexOf(state) > -1;
};

export const isPershingVoluntaryState = (state) => {
  return pershingVoluntaryStateWithholdings.includes(state);
};

/**
 * Apply styles on Beacon shadow elements
 * @param {element} elementHostId
 * @param {string} shadowSelector
 * @param {string} width
 */
export const setStyleShadowEl = (elementHostId, shadowSelector, width) => {
  const $el =
    document.getElementById(elementHostId) ||
    document.querySelector(shadowSelector);
  if ($el) {
    const socialSecurityLabel = $el.shadowRoot?.querySelector(shadowSelector);
    socialSecurityLabel.style.width = width;
    socialSecurityLabel.style.display = 'block';
  }
};

// export function isRepFromSalesforce() {
//   return (
//     window.location.pathname.includes('salesforce') &&
//     window.location.host.includes('awstrp')
//   );
// }

export const isLocalEnvironmentUrl = () =>
  !!window &&
  ['localhost', 'localhost.troweprice.com', '127.0.0.1'].includes(
    window.location.hostname.toLowerCase()
  );

export const isDevEnvironmentUrl = () =>
  !!window && window.location.hostname.toLowerCase().startsWith('wwwdev');

export const isStageEnvironmentUrl = () =>
  !!window && window.location.hostname.toLowerCase().startsWith('wwwstage');

export const isNonProductionUrl = () =>
  isLocalEnvironmentUrl() || isDevEnvironmentUrl() || isStageEnvironmentUrl();

export const isPromise = (value) => !!value && typeof value.then === 'function';
